/**
 * Schutzklassen: Vorschlag, Abweichung, Auf- und Abstufung.
 *
 * Kennt weder DOM noch Supabase noch React.
 *
 * ═══ WARUM DER VORSCHLAG NUR EIN VORSCHLAG IST ════════════════════════════
 * Die Schutzklasse ist die Aussage, an der spaeter gemessen wird, ob jemand
 * richtig ausgeruestet war. Ein Automat, der sie stillschweigend setzt, macht
 * den Koordinator zum Verwalter einer Rechnung, die er nicht gefuehrt hat.
 *
 * Ausserdem KANN die Rechnung es gar nicht allein: die Materialklasse ist
 * Abfallrecht. Ob bei einer Taetigkeit ueberhaupt eine Exposition entsteht,
 * entscheidet das Arbeitsverfahren — ein Bagger mit geschlossener,
 * belüfteter Kabine arbeitet in einem BM-F3-Bereich unter Basisschutz, ein
 * Mann mit der Schaufel daneben nicht.
 *
 * Deshalb: der Vorschlag kommt aus der Einstufung, die Entscheidung aus der
 * taetigkeitsbezogenen Beurteilung — und jede Abweichung NACH UNTEN verlangt
 * eine Begruendung im Klartext.
 */

import type { Cluster, Schutzklasse, Stufung, Taetigkeit } from './types';
import type { ClusterEinstufung } from './einstufung';
import { beurteilungsluecken } from './einstufung';
import { ebvKlasse } from './kataloge/ebvKlassen';

// ═══════════════════════════════════════════════════════════════════════════
// RANGFOLGE
// ═══════════════════════════════════════════════════════════════════════════

/** Rang einer Klasse; `-1`, wenn der Schluessel im Plan nicht vorkommt.
 *  Bewusst negativ: eine unbekannte Klasse darf nie als „mindestens
 *  Basisschutz" durchgehen. */
export const rangVon = (key: string, klassen: Schutzklasse[]): number =>
    klassen.find(k => k.key === key)?.rang ?? -1;

/** Die Klasse mit dem naechsthoeheren Rang; `null`, wenn es keine gibt. */
export const naechstHoehere = (key: string, klassen: Schutzklasse[]): Schutzklasse | null => {
    const rang = rangVon(key, klassen);
    if (rang < 0) return null;
    return [...klassen]
        .filter(k => k.rang > rang)
        .sort((a, b) => a.rang - b.rang)[0] ?? null;
};

/** Die Klasse mit dem naechstniedrigeren Rang; `null`, wenn es keine gibt. */
export const naechstNiedrigere = (key: string, klassen: Schutzklasse[]): Schutzklasse | null => {
    const rang = rangVon(key, klassen);
    if (rang < 0) return null;
    return [...klassen]
        .filter(k => k.rang < rang)
        .sort((a, b) => b.rang - a.rang)[0] ?? null;
};

// ═══════════════════════════════════════════════════════════════════════════
// VORSCHLAG
// ═══════════════════════════════════════════════════════════════════════════

export interface Vorschlag {
    /** Vorgeschlagene Klasse; `''`, wenn kein Vorschlag moeglich ist. */
    key: string;
    /** Warum — geht wortgleich in die Oberflaeche und in den Plan. */
    begruendung: string;
    /** Ob der Vorschlag auf einer belastbaren Grundlage steht. */
    belastbar: boolean;
}

/**
 * Schlaegt aus der Einstufung eines Arbeitsbereichs eine Schutzklasse vor.
 *
 * Die Regeln, absichtlich wenige und absichtlich grob:
 *   • vollstaendig beurteilt und nicht belastet → Basisschutz (niedrigster Rang),
 *   • belastet → mittlere Klasse,
 *   • belastet UND durchgaengig ueber die Tiefe → hoechste Klasse,
 *   • Analytik-Vorbehalt → mindestens mittlere Klasse (Vorsorgeprinzip),
 *   • Beurteilungsluecke (Feld ohne Probe oder Probe ohne Einstufung) →
 *     NIE die niedrigste Klasse, und der Vorschlag ist nicht belastbar,
 *   • Bereich ausdruecklich abgegrenzt → kein Vorschlag.
 *
 * Die Grobheit ist Absicht. Eine feinere Automatik taeuschte eine Herleitung
 * vor, die aus einer Materialklasse nicht kommt.
 */
export const schutzklasseVorschlag = (
    cluster: Pick<Cluster, 'art'>,
    einstufung: ClusterEinstufung,
    klassen: Schutzklasse[],
): Vorschlag => {
    if (klassen.length === 0) {
        return { key: '', begruendung: 'Im Plan ist noch keine Schutzklasse angelegt.', belastbar: false };
    }

    const sortiert = [...klassen].sort((a, b) => a.rang - b.rang);
    const niedrigste = sortiert[0];
    const hoechste = sortiert[sortiert.length - 1];
    const mittlere = sortiert[Math.min(1, sortiert.length - 1)];

    if (cluster.art === 'abgegrenzt') {
        return {
            key: '',
            begruendung: 'Bereich ist ausdrücklich als nicht A+S-pflichtig abgegrenzt, '
                + 'es wird keine Schutzklasse vorgeschlagen.',
            belastbar: true,
        };
    }

    const klasse = ebvKlasse(einstufung.klasse);

    // Unbeprobte Felder UND Felder mit unbewerteter Probe. Beides heisst
    // „nicht beurteilt" und darf niemals wie „unbelastet" wirken — genau
    // dieser Kurzschluss hat aus fehlenden Daten eine Unbedenklichkeits-
    // bescheinigung gemacht.
    const luecken = beurteilungsluecken(einstufung);

    if (einstufung.vorbehalt && (!klasse || !klasse.belastet)) {
        return {
            key: mittlere.key,
            begruendung: 'Analytik unvollständig. Nach dem Vorsorgeprinzip wird bis zum '
                + 'Vorliegen der Ergebnisse mindestens der Standardschutz angesetzt.'
                + (luecken.length > 0
                    ? ` Zusätzlich ohne Beurteilung: ${luecken.join(', ')}.`
                    : ''),
            belastbar: luecken.length === 0,
        };
    }

    if (!klasse) {
        return {
            key: '',
            begruendung: luecken.length > 0
                ? `Keine Einstufung: für ${luecken.join(', ')} liegt keine beurteilte Probe vor.`
                : 'Keine Einstufung vorhanden. Zuerst die Befunde erfassen.',
            belastbar: false,
        };
    }

    if (!klasse.belastet) {
        // Der Bereich fuehrt zwar eine unbelastete Klasse, ist aber nicht
        // vollstaendig beurteilt. Die niedrigste Klasse waere hier die
        // gefaehrlichste Antwort: sie steht in der Oberflaeche mit einem
        // Ein-Klick-„übernehmen" daneben und liest sich als Entwarnung.
        if (luecken.length > 0) {
            return {
                key: mittlere.key,
                begruendung: `Höchste beurteilte Einstufung im Bereich ist ${klasse.label} `
                    + `(unbelastet). Beurteilt ist der Bereich damit aber nicht: für `
                    + `${luecken.join(', ')} fehlt sie. Bis zur Klärung gilt mindestens der `
                    + 'Standardschutz (TRGS 524 Nr. 4.1 Abs. 15).',
                belastbar: false,
            };
        }
        return {
            key: niedrigste.key,
            begruendung: `Höchste Einstufung im Bereich ist ${klasse.label} (unbelastet).`,
            belastbar: true,
        };
    }

    if (einstufung.durchgaengig_belastet) {
        const tiefe = einstufung.belastet_bis_tiefe;
        return {
            key: hoechste.key,
            begruendung: `Feld ${einstufung.massgebliches_feld} ist durchgängig belastet`
                + (tiefe !== null ? ` bis ${tiefe.toString().replace('.', ',')} m` : '')
                + ` (${klasse.label}). Es gibt keinen unbelasteten Horizont darunter.`,
            belastbar: true,
        };
    }

    return {
        key: mittlere.key,
        begruendung: `Höchste Einstufung im Bereich ist ${klasse.label} `
            + `(Feld ${einstufung.massgebliches_feld}).`,
        belastbar: true,
    };
};

// ═══════════════════════════════════════════════════════════════════════════
// ABWEICHUNG
// ═══════════════════════════════════════════════════════════════════════════

export interface Abweichung {
    /** Die gewaehlte Klasse liegt unter dem Vorschlag. */
    nach_unten: boolean;
    /** Begruendung fehlt, obwohl sie noetig waere. */
    begruendung_fehlt: boolean;
    text: string;
}

/**
 * Prueft, ob die gewaehlte Klasse vom Vorschlag abweicht — und ob das
 * begruendet ist.
 *
 * Nur die Abweichung NACH UNTEN ist begruendungspflichtig. Wer hoeher
 * schuetzt als vorgeschlagen, muss sich nicht rechtfertigen; das ist immer
 * zulaessig und oft richtig.
 */
export const pruefeAbweichung = (
    cluster: Pick<Cluster, 'schutzklasse_key' | 'schutzklasse_begruendung'>,
    vorschlag: Vorschlag,
    klassen: Schutzklasse[],
): Abweichung => {
    if (!vorschlag.key || !cluster.schutzklasse_key) {
        return { nach_unten: false, begruendung_fehlt: false, text: '' };
    }

    const gewaehlt = rangVon(cluster.schutzklasse_key, klassen);
    const empfohlen = rangVon(vorschlag.key, klassen);
    if (gewaehlt < 0 || empfohlen < 0 || gewaehlt >= empfohlen) {
        return { nach_unten: false, begruendung_fehlt: false, text: '' };
    }

    const fehlt = cluster.schutzklasse_begruendung.trim().length === 0;
    return {
        nach_unten: true,
        begruendung_fehlt: fehlt,
        text: `Gewählt ist ${cluster.schutzklasse_key}, vorgeschlagen war `
            + `${vorschlag.key}. ${vorschlag.begruendung}`
            + (fehlt ? ' Eine Begründung für die Abweichung fehlt.' : ''),
    };
};

// ═══════════════════════════════════════════════════════════════════════════
// AUF- UND ABSTUFUNG
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Prueft eine einzelne Stufung auf Vollstaendigkeit.
 *
 * ═══ DIE ASYMMETRIE IST DER PUNKT ═════════════════════════════════════════
 * AUFstufen darf der Aufsichtsfuehrende sofort und allein, im Anlassfall
 * (Geruch, Verfaerbung, unbekanntes Material, ueberschrittener
 * Schwellenwert). Verlangt wird nur der Anlass und der Eintrag ins
 * Bautagebuch — alles andere hielte im Ernstfall auf.
 *
 * ABstufen darf nur der Koordinator, schriftlich, und nur auf Nachweis:
 * entweder ist der Weissbereich hergestellt (Trennlage und Abdeckung
 * eingebaut) oder mehrere aufeinanderfolgende Schichten haben keine
 * relevante Exposition gezeigt. Ohne diesen Nachweis waere die Abstufung
 * eine Behauptung — und sie wirkt auf alle, die dort danach arbeiten.
 */
export const pruefeStufung = (stufung: Stufung): string[] => {
    const fehler: string[] = [];

    if (!stufung.von_key || !stufung.nach_key) {
        fehler.push('Ausgangs- und Zielklasse angeben.');
    }
    if (!stufung.entschieden_von.trim()) {
        fehler.push('Wer die Änderung veranlasst hat, fehlt.');
    }
    if (!stufung.entschieden_am) {
        fehler.push('Datum der Änderung fehlt.');
    }

    if (stufung.richtung === 'auf') {
        if (!stufung.anlass.trim()) {
            fehler.push('Anlass der Aufstufung angeben (Geruch, Verfärbung, unbekanntes '
                + 'Material, überschrittener Schwellenwert).');
        }
    } else {
        if (!stufung.grundlage.trim()) {
            fehler.push('Grundlage der Abstufung angeben: hergestellter Weißbereich '
                + '(Trennlage und Abdeckung eingebaut) oder Messreihe über mehrere '
                + 'aufeinanderfolgende Schichten.');
        }
        if (!stufung.bestaetigt_von.trim()) {
            fehler.push('Eine Abstufung bedarf der schriftlichen Bestätigung durch den '
                + 'Koordinator nach DGUV Regel 101-004.');
        }
    }

    if (!stufung.bautagebuch) {
        fehler.push('Eintrag im Bautagebuch noch nicht bestätigt.');
    }

    return fehler;
};

/**
 * Die aktuell geltende Klasse eines Arbeitsbereichs.
 *
 * Ausgangspunkt ist die im Plan festgelegte Klasse; danach wirken die
 * Stufungen in ihrer zeitlichen Reihenfolge. Unvollstaendige Stufungen
 * werden UEBERGANGEN und nicht etwa angewandt: eine Abstufung ohne
 * Bestaetigung des Koordinators gilt nicht, auch wenn sie erfasst ist.
 *
 * Stufungen ohne Datum werden ans Ende sortiert — sie sind noch in Arbeit.
 */
export const geltendeKlasse = (
    cluster: Pick<Cluster, 'kennung' | 'schutzklasse_key'>,
    stufungen: Stufung[],
): { key: string; herkunft: string } => {
    const eigene = stufungen
        .filter(s => s.cluster_kennung === cluster.kennung)
        .filter(s => pruefeStufung(s).length === 0)
        .sort((a, b) => (a.entschieden_am ?? '9999').localeCompare(b.entschieden_am ?? '9999'));

    if (eigene.length === 0) {
        return { key: cluster.schutzklasse_key, herkunft: 'Festlegung im Plan' };
    }

    const letzte = eigene[eigene.length - 1];
    return {
        key: letzte.nach_key,
        herkunft: `${letzte.richtung === 'auf' ? 'Aufstufung' : 'Abstufung'} vom `
            + `${letzte.entschieden_am ?? 'ohne Datum'} durch ${letzte.entschieden_von}`,
    };
};

// ═══════════════════════════════════════════════════════════════════════════
// TAETIGKEIT GEGEN BEREICH
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Findet Taetigkeiten, die unter der Klasse ihres Arbeitsbereichs liegen.
 *
 * Das ist ZULAESSIG und oft richtig — der Baggerfahrer in der belüfteten
 * Kabine ist der Standardfall. Es ist aber begruendungspflichtig, und die
 * Begruendung muss an der Taetigkeit stehen: sie liegt im Arbeitsverfahren
 * (geschlossene Kabine, Schutzbelueftung H13, kein Aussteigen im
 * Schwarzbereich), nicht im Stoff.
 */
export const taetigkeitenUnterBereich = (
    taetigkeiten: Taetigkeit[],
    cluster: Cluster[],
    klassen: Schutzklasse[],
): { taetigkeit: Taetigkeit; bereich_key: string; text: string }[] => {
    const bereichKlasse = new Map(cluster.map(c => [c.kennung, c.schutzklasse_key]));

    return taetigkeiten
        .map(t => {
            const bereich = bereichKlasse.get(t.cluster_kennung);
            if (!bereich || !t.schutzklasse_key) return null;
            const rangT = rangVon(t.schutzklasse_key, klassen);
            const rangB = rangVon(bereich, klassen);
            if (rangT < 0 || rangB < 0 || rangT >= rangB) return null;
            return {
                taetigkeit: t,
                bereich_key: bereich,
                text: `„${t.taetigkeit}“ ist mit ${t.schutzklasse_key} geführt, der Bereich `
                    + `${t.cluster_kennung} mit ${bereich}. Das ist zulässig, wenn das `
                    + 'Arbeitsverfahren die Exposition verhindert; die Begründung gehört '
                    + 'in das Feld Arbeitsverfahren.',
            };
        })
        .filter((x): x is { taetigkeit: Taetigkeit; bereich_key: string; text: string } => x !== null);
};
